/**
 * The publish gate, shown live next to the guide being edited.
 *
 * This panel and the `onPublish` function call the same
 * `evaluatePublishGate` from `@zennopay/schema`, so they cannot drift into
 * disagreeing about what is publishable. The panel is still only a courtesy:
 * it renders in a browser and a browser can be made to render anything. What
 * actually stops a guide is the function, and behind it `firestore.rules`.
 */

import React, { useMemo, useRef } from "react";
import { EntityCustomViewParams } from "@firecms/core";
import { Alert, CenteredView, Chip, CircularProgress, Typography } from "@firecms/ui";
import { evaluatePublishGate, GateIssue } from "@zennopay/schema";

import type { GuideDocument } from "../collections/guides";
import { fillEmptyDerivable, withPublishDefaults } from "../derive/autofill";
import { SITE_ORIGIN } from "../fields/useDerivation";
import { useGateContext, useGateReferenceData } from "./useGateContext";

function IssueList({ issues, tone }: { issues: GateIssue[]; tone: "error" | "warning" | "info" }) {
    return (
        <div className={"flex flex-col gap-2"}>
            {/* Indexed, because code and field are not a unique pair: two broken
                links in one body are both `link.broken` on `body`, and React
                then drops one of them from the list it renders. */}
            {issues.map((issue, index) => (
                <Alert key={`${issue.code}:${issue.field}:${index}`} color={tone} size={"small"}>
                    <div className={"flex flex-col gap-1"}>
                        <Typography variant={"body2"}>{issue.message}</Typography>
                        <div className={"flex flex-row gap-2 items-center"}>
                            <Chip size={"small"}>{issue.field}</Chip>
                            <Typography variant={"caption"} className={"font-mono opacity-60"}>
                                {issue.code}
                            </Typography>
                        </div>
                    </div>
                </Alert>
            ))}
        </div>
    );
}

function Section({ title, blurb, children }: {
    title: string;
    blurb: string;
    children: React.ReactNode;
}) {
    return (
        <section className={"flex flex-col gap-2"}>
            <Typography variant={"subtitle1"}>{title}</Typography>
            <Typography variant={"caption"} className={"opacity-70"}>{blurb}</Typography>
            {children}
        </section>
    );
}

export function PublishGateView({ entity, modifiedValues }: EntityCustomViewParams<GuideDocument>) {

    const { data, loading, error } = useGateReferenceData();

    /* `modifiedValues` is what is in the form right now, saved or not. Evaluating
       the saved entity instead would tell an editor whether the guide *was*
       publishable, which is not the question they are asking. */
    const values = (modifiedValues ?? entity?.values ?? {}) as Partial<GuideDocument>;

    /* Held still for the life of the panel: a fresh timestamp on every keystroke
       would rebuild the context and re-evaluate the gate for no reason. */
    const previewNow = useRef(new Date().toISOString());

    /**
     * The guide as the save will write it, not as the form holds it.
     *
     * `onPreSave` derives whatever is still empty and dates the guide at the
     * moment of publication. Evaluating the raw form values instead reports a
     * missing publish date on every new guide, which is a blocking issue that
     * does not exist and that no editor can act on: the field is meant to be
     * left alone. Nothing here is more permissive than the server, because it
     * is the same two functions the server-side save runs.
     */
    const asSaved = useMemo(
        () => withPublishDefaults(fillEmptyDerivable(values, SITE_ORIGIN), previewNow.current),
        [values]
    );

    const context = useGateContext(entity?.id, asSaved as any, data);
    const datedOnPublish = !values.publishedAt;

    if (loading) {
        return <CenteredView><CircularProgress/></CenteredView>;
    }

    if (error) {
        return (
            <CenteredView>
                <Alert color={"error"}>
                    Could not load the sources, claims, and people this guide depends on, so the
                    gate cannot be evaluated: {error.message}
                </Alert>
            </CenteredView>
        );
    }

    const result = evaluatePublishGate(context);
    const errors = result.issues.filter((issue) => issue.severity === "error");
    const warnings = result.issues.filter((issue) => issue.severity === "warning");

    return (
        <div className={"flex flex-col gap-6 p-8 max-w-4xl mx-auto w-full"}>

            <Alert color={result.canPublish ? "success" : "error"}>
                <Typography variant={"subtitle2"}>
                    {result.canPublish
                        ? "This guide can be published."
                        : `This guide cannot be published: ${errors.length} blocking issue${errors.length === 1 ? "" : "s"}.`}
                </Typography>
            </Alert>

            {errors.length > 0 && (
                <Section title={"Blocking"}
                         blurb={"Publishing is refused until every one of these is resolved."}>
                    <IssueList issues={errors} tone={"error"}/>
                </Section>
            )}

            {warnings.length > 0 && (
                <Section title={"Warnings"}
                         blurb={"These do not block publishing. They are worth fixing anyway."}>
                    <IssueList issues={warnings} tone={"warning"}/>
                </Section>
            )}

            {/* Waived issues are errors that a migration exemption downgraded. They
                are shown apart from real warnings because they are not judgement
                calls someone made about this content: they are debt inherited from
                a page that was already public before the CMS existed. */}
            {result.waived.length > 0 && (
                <Section title={"Waived by migration exemption"}
                         blurb={
                             "Errors this guide's exemption explicitly covers, recorded at import. " +
                             "They are outstanding debt, not absolution, and they keep the guide in " +
                             "the review queue. Anything not named in the exemption still blocks."
                         }>
                    <IssueList issues={result.waived} tone={"info"}/>
                </Section>
            )}

            {result.canPublish && warnings.length === 0 && result.waived.length === 0 && (
                <Typography variant={"body2"} className={"opacity-70"}>
                    Nothing outstanding.
                </Typography>
            )}

            <Typography variant={"caption"} className={"opacity-60"}>
                Evaluated in the browser against {context.claims.length} claim(s) and{" "}
                {context.sources.length} attached source(s), on the guide as saving it would
                write it: derived fields filled in
                {datedOnPublish ? ", and dated at the moment you publish" : ""}. The same
                function runs server-side on publish, which is the check that actually decides.
            </Typography>
        </div>
    );
}
